import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, FileText, X } from "lucide-react";
import { fetchInvoices, formatApiError } from "@/lib/api";
import InvoiceSheet from "@/components/InvoiceSheet";
import { toast } from "sonner";

const STATUS_BADGE = {
  draft: "border-zinc-500/60 text-zinc-400",
  sent: "border-steel-brass/60 text-steel-brass",
  paid: "border-emerald-500/60 text-emerald-400",
  estimate: "border-amber-500/60 text-amber-400",
};

const money = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

function InvoiceRow({ inv, onView }) {
  const isEstimate = inv.status === "estimate";
  return (
    <button
      type="button"
      data-testid={`client-invoice-view-${inv.id}`}
      onClick={() => onView(inv)}
      className="flex w-full flex-wrap items-center justify-between gap-3 border border-steel-border bg-steel-surface px-5 py-4 text-left transition-colors duration-300 hover:border-steel-muted"
    >
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <span className="font-mono text-sm font-semibold text-steel-text">{inv.number}</span>
        <span className={`border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] ${STATUS_BADGE[inv.status]}`}>
          {inv.status === "draft" && inv.kind === "mobile" ? "Unpaid" : inv.status}
        </span>
        <span className="text-xs text-steel-muted">{fmtDate(inv.created_at)}</span>
      </div>
      <span className="font-mono text-sm text-steel-brass">
        {isEstimate
          ? `$${Number(inv.estimate_min || 0).toLocaleString()} – $${Number(inv.estimate_max || inv.total).toLocaleString()}`
          : money(inv.total)}
      </span>
    </button>
  );
}

export default function ClientInvoicesTab({ adminKey, client }) {
  const [all, setAll] = useState(null);
  const [view, setView] = useState(null);

  const load = useCallback(async () => {
    try {
      setAll((await fetchInvoices(adminKey)).invoices);
    } catch (e) {
      toast.error(formatApiError(e));
      setAll([]);
    }
  }, [adminKey]);

  useEffect(() => {
    load();
  }, [load]);

  const email = (client.email || "").toLowerCase();
  const records = useMemo(() => {
    if (!all) return null;
    return all.filter(
      (inv) =>
        (inv.client_user_id && inv.client_user_id === client.user_id) ||
        (email && (inv.recipient_email || "").toLowerCase() === email)
    );
  }, [all, client.user_id, email]);

  const estimates = useMemo(() => (records || []).filter((r) => r.status === "estimate"), [records]);
  const invoices = useMemo(() => (records || []).filter((r) => r.status !== "estimate"), [records]);

  if (records === null) {
    return (
      <div className="mt-6 flex items-center gap-2 text-sm text-steel-muted" data-testid="client-invoices-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading billing…
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-8" data-testid="client-invoices-tab">
      {records.length === 0 && (
        <div className="flex flex-col items-start gap-4 border border-dashed border-steel-border bg-steel-surface p-10" data-testid="client-invoices-empty">
          <FileText className="h-6 w-6 text-steel-muted" />
          <p className="text-sm text-steel-secondary">No invoices or estimates for this client yet.</p>
        </div>
      )}

      {invoices.length > 0 && (
        <div>
          <h4 className="mb-3 font-mono text-[11px] uppercase tracking-[0.25em] text-steel-muted">
            Invoices ({invoices.length})
          </h4>
          <div className="space-y-3">
            {invoices.map((inv) => (
              <InvoiceRow key={inv.id} inv={inv} onView={setView} />
            ))}
          </div>
        </div>
      )}

      {estimates.length > 0 && (
        <div>
          <h4 className="mb-3 font-mono text-[11px] uppercase tracking-[0.25em] text-steel-muted">
            Estimates ({estimates.length})
          </h4>
          <div className="space-y-3">
            {estimates.map((inv) => (
              <InvoiceRow key={inv.id} inv={inv} onView={setView} />
            ))}
          </div>
        </div>
      )}

      {view && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm"
          data-testid="client-invoice-modal"
          onClick={() => setView(null)}
        >
          <div className="my-8 w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex justify-end">
              <button
                data-testid="client-invoice-modal-close"
                onClick={() => setView(null)}
                className="flex items-center gap-1.5 border border-steel-border bg-steel-surface px-4 py-2 text-xs text-steel-secondary transition-colors hover:text-steel-text"
              >
                <X className="h-3.5 w-3.5" /> Close
              </button>
            </div>
            <InvoiceSheet invoice={view} />
          </div>
        </div>
      )}
    </div>
  );
}
